import { useEffect, useState } from 'react'
import { faDownload } from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import Icon from '../../components/icon/Icon'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import { getMyDocuments } from '../../_data/resident'
import { ResidentDocument } from '../../_types/resident'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  DocumentList,
  DocumentRow,
  DocumentInfo,
  DocumentName,
  DocumentMeta,
  DownloadLink,
} from './styled'

/**
 * The renter's Documents vault for the selected residence: the lease, notices,
 * and receipts the property manager has shared, read-only with a download link
 * per file. Keyed on the selected tenancy so switching residence reloads the
 * right set. The renter never uploads — only the PM attaches documents.
 *
 * @returns {JSX.Element}
 */
const DocumentsPage = (): JSX.Element => {
  const { selectedTenancy, selectedTenancyId, isLoadingTenancies } =
    useResidentTenancy()
  const [documents, setDocuments] = useState<ResidentDocument[]>([])
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false)

  useEffect(() => {
    if (selectedTenancyId === '') {
      setDocuments([])
      setIsLoadingDocuments(false)
      return
    }

    let isMounted = true
    setDocuments([])
    setIsLoadingDocuments(true)

    void getMyDocuments(selectedTenancyId).then(loaded => {
      if (isMounted === false) {
        return
      }
      setDocuments(loaded)
      setIsLoadingDocuments(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  const hasDocuments = documents.length > 0

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <PageHeader>
          <PageTitle>Documents</PageTitle>
          <PageSubtitle>
            Your lease, notices, and receipts — view or download any time.
          </PageSubtitle>
        </PageHeader>

        <TenancySwitcher />

        {isLoadingTenancies === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading…</QuietText>
          </CardSurface>
        ) : null}

        {isLoadingTenancies === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have any tenancies yet. When your property manager
              invites you, claim the invite from your email to see your
              documents here.
            </QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null && isLoadingDocuments === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading documents…</QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null &&
        isLoadingDocuments === false &&
        hasDocuments === false ? (
          <CardSurface padding="loose">
            <QuietText>
              No documents yet. When your property manager shares your lease, a
              notice, or a receipt, it shows up here.
            </QuietText>
          </CardSurface>
        ) : null}

        {hasDocuments === true ? (
          <DocumentList>
            {documents.map(residentDocument => (
              <CardSurface key={residentDocument.id} padding="standard">
                <DocumentRow>
                  <DocumentInfo>
                    <DocumentName>{residentDocument.name}</DocumentName>
                    <DocumentMeta>{residentDocument.category}</DocumentMeta>
                  </DocumentInfo>
                  <DownloadLink
                    href={residentDocument.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon={faDownload} />
                    Download
                  </DownloadLink>
                </DocumentRow>
              </CardSurface>
            ))}
          </DocumentList>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default DocumentsPage
